import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Table, Button, Form, Image, Spinner, Alert, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

export default function BlogListPage() {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    const getImageUrl = (path) => {
        if (!path) return "";
        return path.startsWith("http") ? path : `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    const fetchBlogs = useCallback(async (searchQuery = "") => {
        try {
            setLoading(true);
            setError("");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/web-manager/blogs`, {
                headers: getAuthHeaders(),
                params: searchQuery ? { search: searchQuery } : {},
            });
            setBlogs(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load blogs.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchBlogs(search);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this blog post?")) return;

        try {
            setError("");
            await axios.delete(`${process.env.REACT_APP_API_URL}/web-manager/blogs/${id}`, {
                headers: getAuthHeaders(),
            });
            setMessage("Blog post deleted successfully.");
            fetchBlogs(search);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete blog post.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Blog Management</h2>
                        <p className="text-muted mb-0">Manage website articles, news</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" className="px-4 fw-semibold" onClick={() => navigate("/blogs/create")}>
                            + Add New Blog
                        </Button>
                    </Col>
                </Row>

                {message && <Alert variant="success" dismissible onClose={() => setMessage("")}>{message}</Alert>}
                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Form onSubmit={handleSearchSubmit}>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by title, description, keywords, or meta..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="bg-light border-0 py-2 px-3"
                                />
                                <Button type="submit" variant="primary" className="px-4">
                                    Search
                                </Button>
                                {search && (
                                    <Button variant="secondary" onClick={() => { setSearch(""); fetchBlogs(""); }}>
                                        Reset
                                    </Button>
                                )}
                            </InputGroup>
                        </Form>
                    </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-2 text-muted">Loading blog posts...</div>
                            </div>
                        ) : blogs.length === 0 ? (
                            <div className="text-center py-5 text-muted">No blog posts found.</div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4">#</th>
                                        <th>Image</th>
                                        <th>Title & Slug</th>
                                        <th>Publish Date</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blogs.map((blog, index) => (
                                        <tr key={blog.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td>
                                                {blog.image ? (
                                                    <Image
                                                        src={getImageUrl(blog.image)}
                                                        alt={blog.name}
                                                        rounded
                                                        style={{ width: "60px", height: "45px", objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <span className="badge bg-secondary">No Image</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="fw-semibold text-dark">{blog.name}</div>
                                                <small className="text-muted d-block">{blog.slug}</small>
                                            </td>
                                            <td>{blog.date ? new Date(blog.date).toLocaleDateString() : "N/A"}</td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => navigate(`/blogs/edit/${blog.id}`, { state: { blog } })}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(blog.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
}